/**
 * Central plan registry — the single source of truth for what each billing
 * tier can do. Every "what is this org allowed to do" question (server tRPC
 * routers, mastra tools, and the frontend upgrade prompts) resolves through
 * this file, so changing a limit or adding a plan is a one-file edit.
 *
 * The free plan is **implicit**: an org with no active subscription row (or a
 * lapsed / canceled one) is treated as `"free"`. Paid tiers (`pro`, `max`)
 * correspond to the Stripe plans configured in `@repo/auth`.
 */

/** The set of billing plans an organization can be on. */
export const PLAN_IDS = ["free", "pro", "max"] as const;

export type PlanId = (typeof PLAN_IDS)[number];

/**
 * Per-plan capability limits. A `null` value means **unlimited**; a number is
 * a hard cap on the count of that resource.
 */
export interface PlanLimits {
  /**
   * Max number of user-connected (BYO) datasources. Sample and Mora-managed
   * datasources never count against this. `null` = unlimited.
   */
  datasources: number | null;
  /**
   * Max number of Fivetran-backed connections. `0` disables Fivetran entirely
   * (the free plan). `null` = unlimited.
   */
  fivetranConnections: number | null;
  /** Whether the org may provision a Mora-managed BigQuery warehouse. */
  managedWarehouse: boolean;
}

/**
 * The limits for each plan. Tweak numbers here to change entitlements across
 * the whole app.
 *
 * - `free`  — 1 BYO datasource and/or the managed database; no Fivetran.
 * - `pro`   — unlimited datasources; 1 Fivetran connection.
 * - `max`   — unlimited datasources; 5 Fivetran connections.
 */
export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  free: { datasources: 1, fivetranConnections: 0, managedWarehouse: true },
  pro: { datasources: null, fivetranConnections: 1, managedWarehouse: true },
  max: { datasources: null, fivetranConnections: 5, managedWarehouse: true },
};

/** Returns the limits for a plan, defaulting to the free plan. */
export const getPlanLimits = (plan: PlanId | string | null | undefined) => {
  if (plan && plan in PLAN_LIMITS) return PLAN_LIMITS[plan as PlanId];
  return PLAN_LIMITS.free;
};

/**
 * Marketing/display copy for each plan, shown in the upgrade dialog and plan
 * cards. Kept alongside the limits so adding or re-pricing a plan is a
 * one-file change. (Actual Stripe prices come from the price IDs in
 * `@repo/auth`; these labels are display-only.)
 */
/** Which kind of logo stack renders inline after a feature label. */
export type PlanFeatureLogos = "database" | "connector";

/**
 * A single line item in a plan card's feature list. Beyond a plain `label`, a
 * feature can carry a secondary `note`, indented `sub` lines, an inline logo
 * stack, or a promotional `callout` box (e.g. the AI-credit offer).
 */
export interface PlanFeature {
  label: string;
  /** Small secondary line under the label, e.g. "Hourly data sync". */
  note?: string;
  /** Indented sub-features, e.g. ["Unlimited editors", "Unlimited dashboards"]. */
  sub?: string[];
  /** Inline logo stack rendered after the label. */
  logos?: PlanFeatureLogos;
  /**
   * Promo callout box, e.g.
   * `{ icon: "stars", label: "$400 worth of Opus 4.8", note: "Limited Time" }`.
   * `icon` is a base icon name (kept as a string so this package stays UI-free).
   */
  callout?: { icon?: string; label: string; note?: string };
}

export interface PlanDisplay {
  label: string;
  /** Headline price, e.g. "$0" / "$200" (no period suffix). */
  price: string;
  /** Billing-period suffix rendered muted after the price, e.g. "/month". */
  priceSuffix?: string;
  tagline: string;
  /** Highlights the card as the recommended tier ("Most Popular"). */
  popular?: boolean;
  /** Structured feature list shown on the plan card. */
  features: PlanFeature[];
}

export const PLAN_DISPLAY: Record<PlanId, PlanDisplay> = {
  free: {
    label: "Free",
    price: "$0",
    tagline: "Explore Mora with a single connection.",
    features: [
      { label: "1 database", logos: "database" },
      { label: "No connectors" },
      { label: "Full platform access" },
    ],
  },
  pro: {
    label: "Starter",
    price: "$200",
    priceSuffix: "/month",
    tagline: "Connect your data, get answers fast.",
    features: [
      { label: "Unlimited databases", logos: "database" },
      { label: "1 connector", logos: "connector", note: "Hourly data sync" },
      {
        label: "Full platform access",
        sub: ["Unlimited editors", "Unlimited dashboards"],
      },
      {
        label: "200 AI credits per month",
        callout: {
          // icon: "stars",
          label: "$400 worth of Opus 4.8",
          note: "Limited Time",
        },
      },
      { label: "Email, chat & Slack support" },
    ],
  },
  max: {
    label: "Growth",
    price: "$500",
    priceSuffix: "/month",
    popular: true,
    tagline: "Scale your analytics across teams.",
    features: [
      { label: "Unlimited databases", logos: "database" },
      {
        label: "5 connectors",
        logos: "connector",
        note: "15 minute data sync",
      },
      {
        label: "Full platform access",
        sub: ["Unlimited editors", "Unlimited dashboards"],
      },
      {
        label: "500 AI credits per month",
        callout: {
          // icon: "stars",
          label: "$1k worth of Opus 4.8",
          note: "Limited Time",
        },
      },
      { label: "Email, chat & Slack support" },
      { label: "API / MCP access" },
    ],
  },
};

/** Display label for a plan id, falling back to a capitalized id if unknown. */
export const getPlanLabel = (
  plan: PlanId | string | null | undefined,
): string => {
  if (plan && plan in PLAN_DISPLAY) return PLAN_DISPLAY[plan as PlanId].label;
  const s = typeof plan === "string" && plan.length ? plan : "free";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** The paid plans an org can upgrade to, in display order. */
export const UPGRADEABLE_PLANS = [
  "pro",
  "max",
] as const satisfies readonly PlanId[];

/**
 * Tier ordering used to classify a plan switch as an upgrade or downgrade.
 * Higher number = higher tier.
 */
export const PLAN_RANK: Record<PlanId, number> = { free: 0, pro: 1, max: 2 };

/** The direction of a plan switch relative to the org's current plan. */
export type PlanChange = "current" | "upgrade" | "downgrade";

/**
 * Classifies `target` relative to `current` so the plans UI can label the
 * action (Current plan / Upgrade / Downgrade) without hardcoding tiers.
 */
export const getPlanChange = (current: PlanId, target: PlanId): PlanChange =>
  target === current
    ? "current"
    : PLAN_RANK[target] > PLAN_RANK[current]
      ? "upgrade"
      : "downgrade";

/**
 * Machine-readable reasons a plan-limit guard can reject an action. Server-side
 * guards throw a `FORBIDDEN` error whose message is one of these tokens so the
 * client can reliably detect a limit hit and surface an upgrade prompt.
 */
export const PLAN_LIMIT_CODES = {
  datasources: "PLAN_LIMIT_DATASOURCES",
  fivetranConnections: "PLAN_LIMIT_FIVETRAN",
} as const;

export type PlanLimitCode =
  (typeof PLAN_LIMIT_CODES)[keyof typeof PLAN_LIMIT_CODES];

/** True when a thrown error message is one of the plan-limit tokens. */
export const isPlanLimitCode = (value: unknown): value is PlanLimitCode =>
  typeof value === "string" &&
  (Object.values(PLAN_LIMIT_CODES) as string[]).includes(value);

/**
 * Given the current count of a limited resource and the plan's limit, reports
 * whether the org is at (or over) its cap. `null` (unlimited) is never at cap.
 */
export const isAtLimit = (current: number, limit: number | null) =>
  limit != null && current >= limit;
